// components/ui/BulkUploadErrors.tsx
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
import {
 AlertTriangle, X, ChevronDown, ChevronUp, Download,
 FileSpreadsheet, Search, Filter, ArrowUpDown,
 ExternalLink
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';

// ============================================================
// TYPES
// ============================================================

export interface BulkUploadErrorDetail {
 row: number;
 field: string;
 message: string;
 suggestedFix?: string;
}

interface BulkUploadErrorsProps {
 isOpen: boolean;
 onClose: () => void;
 errors: BulkUploadErrorDetail[];
 totalRows: number;
 successCount: number;
 errorCount: number;
 categoriesCreated?: number;
 onDownloadReport?: () => void;
}

type SortField = 'row' | 'field' | 'message';
type SortDirection = 'asc' | 'desc';

// ============================================================
// ERROR SEVERITY HELPER
// ============================================================

function getErrorSeverity(message: string): 'critical' | 'warning' | 'info' {
 const lower = message.toLowerCase();
 if (
 lower.includes('required') ||
 lower.includes('must be') ||
 lower.includes('invalid') ||
 lower.includes('already exists') ||
 lower.includes('not found')
 ) {
 return 'critical';
 }
 if (
 lower.includes('should') ||
 lower.includes('consider') ||
 lower.includes('typically') ||
 lower.includes('warning')
 ) {
 return 'warning';
 }
 return 'info';
}

function getSeverityStyles(severity: 'critical' | 'warning' | 'info'): {
 bg: string;
 border: string;
 text: string;
 badge: string;
} {
 switch (severity) {
 case 'critical':
 return {
 bg: 'bg-rose-50',
 border: 'border-rose-200',
 text: 'text-rose-700',
 badge: 'bg-rose-100 text-rose-700',
 };
 case 'warning':
 return {
 bg: 'bg-amber-50',
 border: 'border-amber-200',
 text: 'text-amber-700',
 badge: 'bg-amber-100 text-amber-700',
 };
 case 'info':
 return {
 bg: 'bg-blue-50',
 border: 'border-blue-200',
 text: 'text-blue-700',
 badge: 'bg-blue-100 text-blue-700',
 };
 }
}

// ============================================================
// COMPONENT
// ============================================================

export function BulkUploadErrors({
 isOpen,
 onClose,
 errors,
 totalRows,
 successCount,
 errorCount,
 categoriesCreated = 0,
 onDownloadReport,
}: BulkUploadErrorsProps) {
 const [searchTerm, setSearchTerm] = useState('');
 const [severityFilter, setSeverityFilter] = useState<'all' | 'critical' | 'warning' | 'info'>('all');
 const [sortField, setSortField] = useState<SortField>('row');
 const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
 const [expandedErrors, setExpandedErrors] = useState<Set<number>>(new Set());

 // Filter and sort errors
 const filteredErrors = errors
 .filter((error) => {
 if (searchTerm) {
 const term = searchTerm.toLowerCase();
 return (
 error.field.toLowerCase().includes(term) ||
 error.message.toLowerCase().includes(term) ||
 String(error.row).includes(term) ||
 (error.suggestedFix && error.suggestedFix.toLowerCase().includes(term))
 );
 }
 return true;
 })
 .filter((error) => {
 if (severityFilter === 'all') return true;
 return getErrorSeverity(error.message) === severityFilter;
 })
 .sort((a, b) => {
 let comparison = 0;
 if (sortField === 'row') comparison = a.row - b.row;
 else if (sortField === 'field') comparison = a.field.localeCompare(b.field);
 else if (sortField === 'message') comparison = a.message.localeCompare(b.message);
 return sortDirection === 'asc' ? comparison : -comparison;
 });

 const toggleSort = (field: SortField) => {
 if (sortField === field) {
 setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
 } else {
 setSortField(field);
 setSortDirection('asc');
 }
 };

 const toggleExpand = (idx: number) => {
 setExpandedErrors((prev) => {
 const next = new Set(prev);
 next.has(idx) ? next.delete(idx) : next.add(idx);
 return next;
 });
 };

 const criticalCount = errors.filter((e) => getErrorSeverity(e.message) === 'critical').length;
 const warningCount = errors.filter((e) => getErrorSeverity(e.message) === 'warning').length;
 const infoCount = errors.filter((e) => getErrorSeverity(e.message) === 'info').length;

 return (
 <AnimatePresence>
 {isOpen && (
 <motion.div
 initial={{ opacity: 0 }}
 animate={{ opacity: 1 }}
 exit={{ opacity: 0 }}
 className="fixed inset-0 z-50 flex items-center justify-center p-4"
 >
 <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
 <motion.div
 initial={{ scale: 0.95, opacity: 0, y: 20 }}
 animate={{ scale: 1, opacity: 1, y: 0 }}
 exit={{ scale: 0.95, opacity: 0, y: 20 }}
 transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                    className="relative bg-white dark:bg-slate-900 border border-transparent dark:border-slate-800 text-slate-900 dark:text-slate-100 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col"
                >
                    {/* ========== HEADER ========== */}
                    <div className="flex-shrink-0 px-6 py-4 bg-gradient-to-r from-rose-600 to-red-700">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="p-1.5 rounded-lg bg-white/20">
                                    <AlertTriangle size={16} className="text-white" />
                                </div>
                                <div>
                                    <h2 className="text-lg font-bold text-white">Upload Error Report</h2>
                                    <p className="text-rose-100 text-xs">
                                        {errorCount} error{errorCount !== 1 ? 's' : ''} found in {totalRows} row{totalRows !== 1 ? 's' : ''}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={onClose}
                                className="p-1.5 rounded-lg bg-white/20 text-white hover:bg-white/30 transition-colors"
                                type="button"
                            >
                                <X size={16} />
                            </button>
                        </div>
                    </div>

                    {/* ========== SUMMARY BAR ========== */}
                    <div className="flex-shrink-0 px-6 py-3 bg-slate-50 dark:bg-slate-950/60 border-b border-slate-200 dark:border-slate-800">
                        <div className="flex items-center gap-4 flex-wrap">
                            <div className="flex items-center gap-2">
                                <div className="flex items-center gap-1.5">
                                    <div className="w-2 h-2 rounded-full bg-emerald-500" />
                                    <span className="text-xs font-medium text-slate-600 dark:text-slate-300">{successCount} successful</span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <div className="w-2 h-2 rounded-full bg-rose-500" />
                                    <span className="text-xs font-medium text-slate-600 dark:text-slate-300">{errorCount} failed</span>
                                </div>
 {categoriesCreated > 0 && (
 <div className="flex items-center gap-1.5">
 <div className="w-2 h-2 rounded-full bg-blue-500" />
 <span className="text-xs font-medium text-slate-600">{categoriesCreated} categories created</span>
 </div>
 )}
 </div>
 <div className="flex-1" />
 {onDownloadReport && (
 <button
 onClick={onDownloadReport}
 className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-teal-700 bg-teal-50 border border-teal-200 rounded-lg hover:bg-teal-100 transition-colors"
 type="button"
 >
 <Download size={12} />
 Download Excel Report
 </button>
 )}
 </div>
 </div>

 {/* ========== FILTERS ========== */}
 <div className="flex-shrink-0 px-6 py-2 border-b border-slate-100 flex items-center gap-3 flex-wrap">
 <div className="relative flex-1 max-w-xs">
 <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
 <input
 type="text"
 placeholder="Search errors..."
 value={searchTerm}
 onChange={(e) => setSearchTerm(e.target.value)}
 className="w-full pl-8 pr-3 py-1.5 text-xs border border-slate-200 rounded-lg bg-white focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400"
 />
 </div>
 <div className="flex items-center gap-1">
 {[
 { key: 'all' as const, label: 'All', count: errors.length },
 { key: 'critical' as const, label: 'Critical', count: criticalCount },
 { key: 'warning' as const, label: 'Warnings', count: warningCount },
 { key: 'info' as const, label: 'Info', count: infoCount },
 ].map(({ key, label, count }) => (
 <button
 key={key}
 onClick={() => setSeverityFilter(key)}
 className={clsx(
 'px-2.5 py-1 rounded-lg text-[11px] font-medium transition-all border',
 severityFilter === key
 ? key === 'critical'
 ? 'bg-rose-50 text-rose-700 border-rose-200'
 : key === 'warning'
 ? 'bg-amber-50 text-amber-700 border-amber-200'
 : key === 'all'
 ? 'bg-teal-50 text-teal-700 border-teal-200'
 : 'bg-blue-50 text-blue-700 border-blue-200'
 : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'
 )}
 type="button"
 >
 {label} {count > 0 && <span className="opacity-60">({count})</span>}
 </button>
 ))}
 </div>
 </div>

 {/* ========== ERROR LIST ========== */}
 <div className="flex-1 overflow-y-auto">
 {filteredErrors.length === 0 ? (
 <div className="flex flex-col items-center justify-center py-16 text-slate-400">
 <Search size={32} className="mb-3" />
 <p className="text-sm font-medium">No matching errors</p>
 <p className="text-xs mt-1">Try adjusting your search or filter</p>
 </div>
 ) : (
 <div className="divide-y divide-slate-50">
 {/* Table Header */}
 <div className="flex items-center gap-3 px-6 py-2 bg-slate-50/50 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
 <button onClick={() => toggleSort('row')} className="flex items-center gap-1 w-16 hover:text-slate-600 transition-colors" type="button">
 Row {sortField === 'row' && <ArrowUpDown size={10} className={sortDirection === 'desc' ? 'rotate-180' : ''} />}
 </button>
 <button onClick={() => toggleSort('field')} className="flex items-center gap-1 w-32 hover:text-slate-600 transition-colors" type="button">
 Field {sortField === 'field' && <ArrowUpDown size={10} className={sortDirection === 'desc' ? 'rotate-180' : ''} />}
 </button>
 <button onClick={() => toggleSort('message')} className="flex items-center gap-1 flex-1 hover:text-slate-600 transition-colors" type="button">
 Error Description {sortField === 'message' && <ArrowUpDown size={10} className={sortDirection === 'desc' ? 'rotate-180' : ''} />}
 </button>
 <span className="w-32">Suggested Fix</span>
 </div>

 {/* Error Rows */}
 {filteredErrors.map((error, idx) => {
 const severity = getErrorSeverity(error.message);
 const styles = getSeverityStyles(severity);
 const isExpanded = expandedErrors.has(idx);

 return (
 <motion.div
 key={idx}
 initial={{ opacity: 0, x: -10 }}
 animate={{ opacity: 1, x: 0 }}
 transition={{ delay: idx * 0.02 }}
 className={clsx('flex items-start gap-3 px-6 py-3 hover:bg-slate-50 transition-colors cursor-pointer', styles.bg)}
 onClick={() => toggleExpand(idx)}
 >
 <span className="w-16 text-xs font-mono font-bold text-slate-500 tabular-nums flex-shrink-0 pt-0.5">#{error.row}</span>
 <span className="w-32 text-xs font-medium text-slate-700 flex-shrink-0 pt-0.5 truncate">{error.field}</span>
 <div className="flex-1 min-w-0">
 <div className="flex items-start gap-2">
 <div className={clsx('px-2 py-0.5 rounded-full text-[10px] font-semibold flex-shrink-0 mt-0.5', styles.badge)}>
 {severity === 'critical' ? 'ERROR' : severity === 'warning' ? 'WARNING' : 'INFO'}
 </div>
 <div>
 <p className={clsx('text-sm', styles.text)}>
 {isExpanded ? error.message : error.message.length > 120 ? `${error.message.substring(0, 120)}...` : error.message}
 </p>
 {error.message.length > 120 && (
 <button className="text-[10px] text-teal-600 hover:text-teal-700 font-medium mt-0.5" type="button">
 {isExpanded ? 'Show less' : 'Show more'}
 </button>
 )}
 </div>
 </div>
 </div>
 <span className="w-32 text-xs text-slate-500 flex-shrink-0 pt-0.5">
 {error.suggestedFix ? (
 <span className="text-teal-600">{error.suggestedFix}</span>
 ) : (
 <span className="text-slate-400 italic">—</span>
 )}
 </span>
 <div className="flex-shrink-0 pt-0.5">
 {isExpanded ? <ChevronUp size={14} className="text-slate-400" /> : <ChevronDown size={14} className="text-slate-400" />}
 </div>
 </motion.div>
 );
 })}
 </div>
 )}
 </div>

                    {/* ========== FOOTER ========== */}
                    <div className="flex-shrink-0 px-6 py-3 bg-slate-50 dark:bg-slate-950/60 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between">
                        <p className="text-xs text-slate-500 dark:text-slate-400">Showing {filteredErrors.length} of {errors.length} error{errors.length !== 1 ? 's' : ''}</p>
                        <div className="flex items-center gap-2">
                            {onDownloadReport && (
                                <button onClick={onDownloadReport}
                                    className="flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-teal-600 to-teal-700 text-white rounded-xl text-sm font-semibold hover:from-teal-700 hover:to-teal-800 transition-all shadow-lg shadow-teal-500/25"
                                    type="button">
                                    <FileSpreadsheet size={14} /> Download Error Report
                                </button>
                            )}
                            <button onClick={onClose}
                                className="px-4 py-2 border border-slate-300 dark:border-slate-700 rounded-xl text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-white dark:hover:bg-slate-800 transition-colors"
                                type="button">Close</button>
                        </div>
                    </div>
 </motion.div>
 </motion.div>
 )}
 </AnimatePresence>
 );
}